<template>
    <div class="flex flex-col dark:text-white">
        <div class="mt-4 flex flex-wrap items-center gap-4">
            <h1 class="text-2xl font-bold">Nexus</h1>
            <div class="flex items-center gap-3">
                <label class="text-sm font-semibold">Parceiro</label>
                <select
                    v-model="selectedPartnerId"
                    @change="handlePartnerChange"
                    class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                    <option value="all">Todos</option>
                    <option v-for="partner in partners" :key="partner.id" :value="partner.id">
                        {{ partner.name }}
                    </option>
                </select>
            </div>
        </div>

        <div class="grid gap-6 my-4 md:grid-cols-2">
            <card :loading="metricsLoading">
                <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Onboarding Aprovados
                </p>
                <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                    {{ metrics.approved_onboards }}
                </p>
            </card>
            <card :loading="metricsLoading">
                <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Faturamento Total
                </p>
                <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                    {{ $root.formatCurrency(metrics.total_billing) }}
                </p>
            </card>
        </div>

        <div class="border-b border-gray-200 dark:border-gray-700">
            <nav class="-mb-px flex gap-4">
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('onboard')"
                    @click="changeTab('onboard')"
                >
                    Onboarding
                </button>
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('sales')"
                    @click="changeTab('sales')"
                >
                    Vendas
                </button>
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('settings')"
                    @click="changeTab('settings')"
                >
                    Configurações
                </button>
            </nav>
        </div>

        <section v-if="activeTab === 'onboard'" class="mt-4">
            <filters-onboard
                v-model="onboardFilters"
                :loading="onboardLoading"
                :default-dates="defaultOnboardDates"
                :statuses="onboardStatusOptions"
                :person-types="onboardTypeOptions"
                class="mt-4"
                @fetch="handleOnboardFetch"
            />

            <table-component
                class="mt-6"
                :loading="onboardLoading"
                :pagination="onboardPagination"
                :headers="[
                    'ID',
                    'Nome',
                    'E-mail',
                    'Telefone',
                    'Pessoa',
                    'Data de Criação',
                    'Status do Onboarding',
                ]"
                @fetch="fetchOnboards"
            >
                <tr v-for="user in onboardUsers" :key="user.id">
                    <td class="table-cell-sm">
                        <router-link
                            :to="{ name: 'compliance.details', params: { user_id: user.id } }"
                            class="text-indigo-400 hover:underline hover:text-indigo-700 hover:cursor-pointer"
                        >
                            #{{ user.id }}
                        </router-link>
                    </td>
                    <td class="table-cell-sm">{{ user.name }}</td>
                    <td class="table-cell-sm">{{ user.email }}</td>
                    <td class="table-cell-sm">{{ user.formatted_phone }}</td>
                    <td class="table-cell-sm">{{ user.formatted_type }}</td>
                    <td class="table-cell-sm text-xs">{{ formatDateWithoutTime(user.created_at) }}</td>
                    <td class="table-cell-sm">
                        <span class="flex items-center">
                            <span
                                class="mr-2 h-2.5 w-2.5 rounded-full"
                                :class="statusDotClass(user.status)"
                            ></span>
                            {{ getStatusName(user.status) }}
                        </span>
                    </td>
                </tr>
            </table-component>
        </section>

        <section v-else-if="activeTab === 'sales'" class="mt-4">
            <filters-orders
                v-model="ordersFilters"
                :loading="ordersLoading"
                date-filter
                :default-dates="defaultOrderDates"
                status-filter
                payment-method-filter
                producer-email-filter
                class="mt-4"
                @fetch="handleOrdersFetch"
            />

            <table-component
                :loading="ordersLoading"
                :pagination="ordersPagination"
                :headers="[
                    'Pedido',
                    'Produto',
                    'Produtor',
                    'Cliente',
                    'Método de Pagamento',
                    'Valor do Pedido',
                    'Data do Pedido',
                    'Status',
                ]"
                @fetch="fetchOrders"
            >
                <tr v-for="order in orders" :key="order.id">
                    <td class="table-cell-sm">
                        <router-link
                            :to="{ name: 'user.order', params: { order_id: order.id } }"
                            class="text-indigo-400 hover:underline hover:text-indigo-700 hover:cursor-pointer"
                        >
                            #{{ order.id }}
                        </router-link>
                    </td>
                    <td class="table-cell-sm">
                        {{ order.product_name }}
                    </td>
                    <td class="table-cell-sm">
                        <button
                            class="text-indigo-500 hover:underline"
                            @click.prevent="manageUser(order.producer.id)"
                        >
                            {{ order.producer.name }}
                        </button>
                    </td>
                    <td class="table-cell-sm text-xs">
                        {{ order.customer ? order.customer.name : order.customer_name }}
                    </td>
                    <td class="table-cell-sm">
                        <payment-type
                            :method="order.transactions[0] ? order.transactions[0].payment_method : null"
                            :hasAmountCard2="order.amount_card2"
                            :brandCard1="order.transactions[0] && order.transactions[0].card ? order.transactions[0].card : null"
                            :brandCard2="order.transactions[1] && order.transactions[1].card ? order.transactions[1].card : null"
                        />
                    </td>
                    <td class="table-cell-sm">
                        {{ $root.formatCurrency(order.amount + order.amount_card2) }}
                    </td>
                    <td class="table-cell-sm text-xs">{{ order.created_at }}</td>
                    <td class="table-cell-sm">
                        <status
                            size="sm"
                            :status="order.transaction_status"
                            :isRefundFromHood="order.transactions[order.transactions.length - 1].is_refund_from_hood"
                        />
                    </td>
                </tr>
            </table-component>
        </section>

        <section v-else class="mt-4">
            <div v-if="selectedPartnerId === 'all'" class="rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800/25">
                Selecione um parceiro para visualizar as configurações.
            </div>
            <div v-else>
                <ticto-loading v-if="settingsLoading" />
                <div v-else class="space-y-4">
                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                        <p class="text-sm font-semibold">URL de arquivos</p>
                        <p class="text-sm text-gray-600 dark:text-gray-300">
                            {{ settings?.settings?.files_domain || '-' }}
                        </p>
                        <p class="mt-3 text-sm font-semibold">URL de Webhook</p>
                        <p class="text-sm text-gray-600 dark:text-gray-300">
                            {{ settings?.settings?.webhook_endpoint || '-' }}
                        </p>
                    </div>

                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25 space-y-3">
                        <form-toggle
                            v-model="settingsForm.client_active"
                            :loading="settingsSaving"
                            label="Ativar integração total via Nexus"
                            @update:modelValue="updateSettings"
                        />

                        <div class="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                            <form-toggle
                                v-model="limitEnabled"
                                :loading="settingsSaving"
                                label="Limitar quantidade de solicitações de onboarding via Nexus"
                                @update:modelValue="handleLimitToggle"
                            />
                            <div v-if="limitEnabled" class="mt-3 flex flex-wrap items-center gap-3">
                                <input
                                    v-model.number="limitValue"
                                    type="number"
                                    min="1"
                                    class="w-40 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                />
                                <button
                                    class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                    :disabled="settingsSaving"
                                    @click.prevent="saveLimit"
                                >
                                    Salvar limite
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                        <h3 class="text-sm font-semibold mb-3">Eventos de webhooks</h3>
                        <div class="grid gap-2 md:grid-cols-2">
                            <div
                                v-for="event in settings?.available_events || []"
                                :key="event"
                                class="flex items-center justify-between rounded border border-gray-200 p-2 text-sm dark:border-gray-700"
                            >
                                <span>{{ formatEventLabel(event) }}</span>
                                <span
                                    class="rounded px-2 py-1 text-xs font-semibold"
                                    :class="isEventActive(event) ? 'bg-green-600 text-white' : 'bg-red-600 text-white'"
                                >
                                    {{ isEventActive(event) ? 'Habilitado' : 'Desabilitado' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Card from "../../components/layouts/Card.vue";
import FiltersOnboard from "../../components/layouts/FiltersOnboard.vue";
import FiltersOrders from "../../components/layouts/FiltersOrders.vue";
import FormToggle from "../../components/forms/FormToggle.vue";
import PaymentType from "../../components/layouts/PaymentType.vue";
import Status from "../../components/layouts/Status.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";

export default {
    components: {
        Card,
        FiltersOnboard,
        FiltersOrders,
        FormToggle,
        PaymentType,
        Status,
        TableComponent,
        TictoLoading,
    },
    mixins: [HelperMixin],
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            partners: [],
            selectedPartnerId: "all",
            metrics: {
                approved_onboards: 0,
                total_billing: 0,
            },
            metricsLoading: false,
            activeTab: "onboard",
            onboardFilters: {},
            onboardUsers: [],
            onboardLoading: false,
            onboardPagination: {
                total: 0,
                per_page: 25,
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
            },
            ordersFilters: {},
            orders: [],
            ordersLoading: false,
            ordersPagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 1,
            },
            defaultOrderDates: [
                moment().subtract(1, "month").format("DD/MM/YYYY"),
                moment().format("DD/MM/YYYY"),
            ],
            defaultOnboardDates: [
                moment().subtract(1, "month").format("DD/MM/YYYY"),
                moment().format("DD/MM/YYYY"),
            ],
            settingsLoading: false,
            settingsSaving: false,
            settings: null,
            settingsForm: {
                client_active: true,
                user_limit: null,
            },
            limitEnabled: false,
            limitValue: null,
        };
    },
    computed: {
        onboardStatusOptions() {
            return [
                { value: "onboarding-waiting", label: "Aguardando Onboarding" },
                { value: "onboarding-pending", label: "Onboarding Pendente" },
                { value: "onboarding-approved", label: "Onboarding Aprovado" },
                { value: "onboarding-reproved", label: "Onboarding Reprovado" },
                { value: "onboarding-pending-correction", label: "Onboarding Pendente de Correção" },
                { value: "pending", label: "Pendentes de Análise" },
                { value: "pending-correction", label: "Pendentes de Correção" },
                { value: "approved", label: "Aprovado" },
                { value: "reproved", label: "Reprovado" },
            ];
        },
        onboardTypeOptions() {
            return [
                { value: "person", label: "Física" },
                { value: "company", label: "Jurídica" },
            ];
        },
    },
    watch: {
    },
    mounted() {
        this.fetchPartners().then(() => {
            this.fetchMetrics();
            this.fetchOnboards();
        });
    },
    methods: {
        tabClass(tab) {
            return this.activeTab === tab
                ? "border-indigo-500 text-indigo-500"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
        },
        changeTab(tab) {
            this.activeTab = tab;
            if (tab === "onboard") {
                this.fetchOnboards();
            }
            if (tab === "sales") {
                this.handleOrdersFetch();
            }
            if (tab === "settings" && this.selectedPartnerId !== "all") {
                this.fetchSettings();
            }
        },
        partnerParam() {
            return this.selectedPartnerId === "all" ? null : this.selectedPartnerId;
        },
        async fetchPartners() {
            try {
                const { data } = await axios.get("/api/nexus/partners");
                this.partners = data.data || [];
            } catch (error) {
                this.toast.error("Erro ao carregar parceiros Nexus.");
            }
        },
        async fetchMetrics() {
            this.metricsLoading = true;
            try {
                const { data } = await axios.get("/api/nexus/partners/metrics", {
                    params: {
                        partner_id: this.partnerParam(),
                    },
                });
                this.metrics = data;
            } catch (error) {
                this.toast.error("Erro ao carregar indicadores.");
            } finally {
                this.metricsLoading = false;
            }
        },
        handlePartnerChange() {
            this.onboardPagination.current_page = 1;
            this.ordersPagination.current_page = 1;
            this.fetchMetrics();
            if (this.activeTab === "onboard") {
                this.fetchOnboards();
            }
            if (this.activeTab === "sales") {
                this.handleOrdersFetch();
            }
            if (this.activeTab === "settings" && this.selectedPartnerId !== "all") {
                this.fetchSettings();
            }
        },
        statusDotClass(status) {
            if (
                ["approved", "onboarding-approved"].includes(status)
            ) {
                return "bg-green-500";
            }
            if (
                ["reproved", "onboarding-reproved"].includes(status)
            ) {
                return "bg-red-500";
            }
            return "bg-yellow-500";
        },
        async fetchOnboards() {
            this.onboardLoading = true;
            try {
                const params = {
                    partner_id: this.partnerParam(),
                    page: this.onboardPagination.current_page,
                    ...this.onboardFilters,
                };

                const { data } = await axios.get("/api/nexus/partners/onboardings", {
                    params,
                });

                this.onboardUsers = data.data;
                this.onboardPagination = {
                    ...this.onboardPagination,
                    ...data,
                };
            } catch (error) {
                this.toast.error("Erro ao carregar onboardings.");
            } finally {
                this.onboardLoading = false;
            }
        },
        handleOnboardFetch() {
            this.onboardPagination.current_page = 1;
            this.fetchOnboards();
        },
        handleOrdersFetch() {
            this.orders = [];
            this.ordersPagination.current_page = 1;
            this.fetchOrders();
        },
        async fetchOrders() {
            this.ordersLoading = true;
            try {
                const params = {
                    ...this.ordersFilters,
                    partner_id: this.partnerParam(),
                    page: this.ordersPagination.current_page,
                };

                const { data } = await axios.get("/api/nexus/partners/orders", {
                    params,
                });

                this.orders = data.data;
                this.ordersPagination = {
                    ...this.ordersPagination,
                    ...data.meta,
                };
            } catch (error) {
                this.toast.error("Erro ao carregar vendas.");
            } finally {
                this.ordersLoading = false;
            }
        },
        manageUser(userId) {
            window.location.href = `/user/${userId}/commissions`;
        },
        async fetchSettings() {
            if (this.selectedPartnerId === "all") {
                return;
            }
            this.settingsLoading = true;
            try {
                const { data } = await axios.get(
                    `/api/nexus/partners/${this.selectedPartnerId}/settings`,
                );
                this.applySettingsResponse(data);
            } catch (error) {
                this.toast.error("Erro ao carregar configurações.");
            } finally {
                this.settingsLoading = false;
            }
        },
        async updateSettings() {
            if (this.selectedPartnerId === "all") {
                return;
            }
            this.settingsSaving = true;
            try {
                const { data } = await axios.put(
                    `/api/nexus/partners/${this.selectedPartnerId}/settings`,
                    {
                        client_active: this.settingsForm.client_active,
                        user_limit: this.settingsForm.user_limit,
                    },
                );
                this.applySettingsResponse(data);
                this.toast.success("Configurações salvas com sucesso.");
            } catch (error) {
                this.toast.error("Erro ao salvar configurações.");
            } finally {
                this.settingsSaving = false;
            }
        },
        applySettingsResponse(data) {
            this.settings = data;
            this.settingsForm = {
                client_active: data.client.active,
                user_limit: data.settings.user_limit,
            };
            this.limitEnabled = data.settings.user_limit !== null && data.settings.user_limit !== undefined;
            this.limitValue = data.settings.user_limit;
        },
        handleLimitToggle(enabled) {
            if (!enabled) {
                if (!window.confirm("Deseja remover o limite de onboarding via Nexus?")) {
                    this.limitEnabled = true;
                    return;
                }
                this.limitValue = null;
                this.settingsForm.user_limit = null;
                this.updateSettings();
                return;
            }
            this.limitValue = this.limitValue || 1;
        },
        saveLimit() {
            if (!this.limitEnabled) {
                return;
            }
            if (!this.limitValue || this.limitValue < 1) {
                this.toast.error("Informe um limite válido.");
                return;
            }
            if (!window.confirm("Confirma a atualização do limite de onboarding?")) {
                return;
            }
            this.settingsForm.user_limit = this.limitValue;
            this.updateSettings();
        },
        isEventActive(event) {
            return this.settings?.settings?.events?.includes(event);
        },
        formatEventLabel(event) {
            return event
                .split("_")
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
        },
    },
};
</script>
